import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Radio, Smartphone } from 'lucide-react'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { getCoverageEvent, listCoverageEvents, listMyAssignments } from './api'
import { useEventFeeds } from './useEventFeeds'
import { BroadcastPanel } from './BroadcastPanel'
import {
  assignmentMeaning, assignmentRoleLabel, assignmentStatusLabel, eventTypeLabel, placeOf,
} from './vocab'
import type { CoverageFeedResponse } from '@/shared/api/types'

/**
 * Broadcast from this device.
 *
 * A laptop at `localhost` is a secure context by definition, so this page always
 * works on the machine running the console. The phone case goes through
 * `/broadcast`, which is the same panel without the shell around it.
 */
export function CoverageGoLivePage() {
  const canBroadcast = useCan('coverage:broadcast')
  const [params, setParams] = useSearchParams()
  const wanted = params.get('feed')
  const wantedEvent = params.get('event')
  const [tick, setTick] = useState(0)

  const { data, error, loading } = useLoad(
    () => listCoverageEvents({ status: undefined }, 0, 100), [tick],
  )

  /**
   * CANCELLED is excluded; ENDED is not.
   *
   * "Ended" covers a flat battery, a closed tab and a thumb on the wrong control
   * as well as "the rally finished" — so an ended event stays offerable and the
   * server decides. Cancelled is the one status that means somebody decided this
   * is not happening, and offering it would be noise.
   */
  const events = useMemo(
    () => (data?.content ?? []).filter((e) => e.statusCode !== 'CANCELLED'),
    [data],
  )

  /**
   * Resolving `?feed=` needs the EVENT, because the list does not carry feeds.
   *
   * `CoverageEventService.list()` passes `List.of()` for feeds — deliberately, to
   * avoid an N+1 across 200 rows — so scanning `events[].feeds` for the wanted id
   * searches a set of empty arrays and always fails. Every Broadcast button and
   * every copied link now carries `&event=`, and this loads that one event.
   *
   * The scan is kept as a fallback for links minted before that change, and it
   * starts working on its own the day the backend adds feeds to the list.
   */
  const detail = useLoad(
    () => (wanted && wantedEvent ? getCoverageEvent(wantedEvent) : Promise.resolve(null)),
    [wanted, wantedEvent, tick],
  )

  const chosen = useMemo(() => {
    if (!wanted) return null
    const fromDetail = detail.data?.feeds.find((f) => f.id === wanted)
    if (fromDetail && detail.data) return { event: detail.data, feed: fromDetail }
    for (const e of events) {
      const f = e.feeds.find((x) => x.id === wanted)
      if (f) return { event: e, feed: f }
    }
    return null
  }, [wanted, detail.data, events])

  /** Still fetching the named event — not yet a reason to say the link is wrong. */
  const resolving = Boolean(wanted && wantedEvent && detail.loading && !detail.data)

  // The pickers need CAMERAS, which the list does not carry. See useEventFeeds.
  const slots = useEventFeeds(chosen ? [] : events)

  /**
   * What this person has been asked to film.
   *
   * Being assigned creates no camera, so an assignment appeared NOWHERE on this
   * page: somebody told "you are filming the Kamukunji rally" opened Go Live and
   * saw an undifferentiated list of events. The one thing this screen owes them
   * is "here is yours", first, with a way in.
   */
  const mine = useLoad(() => listMyAssignments(), [tick])
  const myOpen = useMemo(
    () => (mine.data ?? []).filter((a) => a.statusCode === 'ASSIGNED' || a.statusCode === 'ACCEPTED'),
    [mine.data],
  )

  if (!canBroadcast) return <Forbidden what="broadcasting" />

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Go Live"
        description="Put this device's camera on air against an open slot."
      />
      <ErrorStrip message={error ?? detail.error} />

      {!chosen && myOpen.length > 0 && (
        <Panel
          title="Assigned to you"
          description="Somebody booked you to film these."
        >
          <ul className="space-y-2">
            {myOpen.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-erp-sm font-medium">{a.eventTitle}</div>
                  <div className="truncate text-erp-xs text-muted-foreground">
                    {assignmentRoleLabel(a.roleCode)} · {assignmentStatusLabel(a.statusCode)}
                    {a.eventScheduledStart
                      && ` · ${new Date(a.eventScheduledStart).toLocaleString()}`}
                  </div>
                  {/* Nobody should have to guess whether they may start. */}
                  <p className="text-erp-xs text-muted-foreground/80">
                    {assignmentMeaning(a.statusCode)}
                  </p>
                </div>
                <AssignedJump eventId={a.eventId} onPick={(feedId) => {
                  const sp = new URLSearchParams(params)
                  sp.set('feed', feedId)
                  sp.set('event', a.eventId)
                  setParams(sp, { replace: true })
                }} />
              </li>
            ))}
          </ul>
          <div className="mt-2">
            <Note tone="info">
              You do not have to accept before filming — accepting holds the camera so
              nobody else claims it. Accept or decline from <strong>My Coverage</strong>.
            </Note>
          </div>
        </Panel>
      )}

      {(loading && !data) || resolving ? <LoadingBlock label="Loading events…" /> : chosen ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <Panel title={chosen.feed.title} description={chosen.event.title}>
            <BroadcastPanel
              feedId={chosen.feed.id}
              feedTitle={chosen.feed.title}
              eventTitle={chosen.event.title}
              compact
              onEnded={() => setTick((t) => t + 1)}
            />
          </Panel>
          <div className="space-y-3">
            <Panel title="Event" dense>
              <dl className="space-y-1.5 text-erp-sm">
                <Row label="Type" value={eventTypeLabel(chosen.event.eventTypeCode)} />
                <Row label="Where" value={placeOf(chosen.event)} />
                <Row label="Starts" value={new Date(chosen.event.scheduledStart).toLocaleString()} />
                <Row label="Reference" value={chosen.event.ref} />
              </dl>
              <div className="mt-3">
                <button type="button" className="erp-btn h-8"
                  onClick={() => {
                  const sp = new URLSearchParams(params)
                  sp.delete('feed'); sp.delete('event')
                  setParams(sp, { replace: true })
                }}>
                  Choose a different camera
                </button>
              </div>
            </Panel>
            <PhoneHandoff feedId={chosen.feed.id} />
          </div>
        </div>
      ) : (
        <>
          {/* A copied link for a camera that was deleted, ended, or sits above
              this caller's clearance would otherwise drop straight to the picker
              with no explanation, and read as a broken button. The wording is
              deliberately the non-leaking one: "any camera you can see". */}
          {/* Only once the lookup has actually finished — see `resolving`. */}
          {wanted && !resolving && (
            <Note tone="bad">
              That link does not match any camera you can see. Pick one below instead.
            </Note>
          )}
          <Panel
            title="Pick a camera to fill"
            description="To film from a phone instead, copy the camera's link from the diary."
          >
            {events.length === 0 ? (
              <Note tone="info">No open events. Create one on the diary first.</Note>
            ) : (
              <>
                <ul className="space-y-3">
                  {events.slice(0, slots.cap).map((e) => (
                    <li key={e.id}>
                      <div className="text-erp-sm font-medium">{e.title}</div>
                      <div className="text-erp-xs text-muted-foreground">{placeOf(e)}</div>
                      <SlotButtons
                        feeds={slots.feeds.get(e.id) ?? []}
                        loading={slots.loading && !slots.feeds.has(e.id)}
                        onPick={(f) => {
                          const sp = new URLSearchParams(params)
                          sp.set('feed', f.id)
                          sp.set('event', e.id)
                          setParams(sp, { replace: true })
                        }}
                      />
                    </li>
                  ))}
                </ul>
                {slots.truncated && (
                  <div className="mt-3">
                    <Note tone="info">
                      Showing the first {slots.cap} open events. Open the rest from the
                      diary and press Broadcast there.
                    </Note>
                  </div>
                )}
              </>
            )}
          </Panel>
          <PhoneHandoff />
        </>
      )}
    </div>
  )
}

/**
 * The way in from an assignment.
 *
 * An assignment names an EVENT, not a camera — `feedId` is nullable and usually
 * null — so this loads the event's cameras and offers them. One request, made
 * only when somebody actually has an assignment on this page.
 */
function AssignedJump({
  eventId, onPick,
}: { eventId: string; onPick: (feedId: string) => void }) {
  const { data, loading } = useLoad(() => getCoverageEvent(eventId), [eventId])
  const open = data?.feeds ?? []

  if (loading && !data) {
    return <span className="text-erp-xs text-muted-foreground">Loading cameras…</span>
  }
  if (open.length === 0) {
    return (
      <span className="text-erp-xs text-muted-foreground">
        No open camera — add one from the diary.
      </span>
    )
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {open.map((f) => (
        <button
          key={f.id}
          type="button"
          className="erp-btn h-9"
          disabled={f.statusCode === 'LIVE'}
          onClick={() => onPick(f.id)}
        >
          <Radio className="h-3.5 w-3.5" />
          {f.title}{f.statusCode === 'LIVE' ? ' (already live)' : ''}
        </button>
      ))}
    </div>
  )
}

function SlotButtons({
  feeds, loading, onPick,
}: {
  feeds: CoverageFeedResponse[]
  loading: boolean
  onPick: (f: CoverageFeedResponse) => void
}) {
  // "Still loading" and "there are none" are different sentences. An empty row
  // that means both is the state somebody stares at wondering if it is broken.
  if (loading) {
    return <p className="mt-1 text-erp-xs text-muted-foreground">Loading cameras…</p>
  }
  // An ENDED camera is offered too — see the events filter above. The only one
  // withheld is the camera already on air, which is disabled rather than hidden.
  const open = feeds
  if (open.length === 0) {
    return (
      <p className="mt-1 text-erp-xs text-muted-foreground">
        No cameras yet — add one from the diary.
      </p>
    )
  }
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {open.map((f) => {
        const live = f.statusCode === 'LIVE'
        return (
          <button
            key={f.id}
            type="button"
            className="erp-btn h-9"
            disabled={live}
            title={live ? 'Already on air' : undefined}
            onClick={() => onPick(f)}
          >
            <Radio className="h-3.5 w-3.5" />
            {f.title}
            {live ? ' (already live)'
              : f.statusCode === 'ENDED' ? ' · again'
              : f.broadcasterName ? ` · ${f.broadcasterName}` : ''}
          </button>
        )
      })}
    </div>
  )
}

function PhoneHandoff({ feedId }: { feedId?: string }) {
  const url = `${window.location.origin}/broadcast${feedId ? `?feed=${feedId}` : ''}`
  return (
    <Panel title="Filming from a phone" dense>
      <p className="text-erp-sm text-muted-foreground">
        Open this on the phone that will do the filming. It signs in with the same account
        and shows only the camera controls.
      </p>
      <code className="mt-2 block break-all rounded-md bg-muted px-2 py-1.5 font-mono text-erp-xs">
        {url}
      </code>
      <div className="mt-2">
        <Note tone="info">
          <span className="inline-flex items-center gap-1">
            <Smartphone className="h-3.5 w-3.5" />
            A phone will not release its camera to a plain <code>http://</code> address.
          </span>{' '}
          If the phone says it cannot open a camera, the console needs to be reached over
          https — the certificate warning on a self-signed one is expected, and the
          connection is still encrypted.
        </Note>
      </div>
    </Panel>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-erp-xs text-muted-foreground">{label}</dt>
      <dd className="min-w-0 flex-1 truncate">{value}</dd>
    </div>
  )
}
